import { css, cx } from '@linaria/core'
import { type Dispatch, type FC, type SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { EnhancedManCodeId } from '#api/enhancements/enhancedTmsApiTypes'
import { useAppNavigation } from '#app/navigation/NavigationContext'
import { useAppDispatch, useAppSelector } from '#app/store'
import { addPendingConfiguration, createLocalConfigurationId } from '#app/store/slices/configurationSlice'
import { Button } from '#components/Button'
import { HoverTooltip } from '#components/HoverTooltip'
import { Icon } from '#components/Icon'
import { IconButton } from '#components/IconButton'
import { Input } from '#components/Input'
import { Select } from '#components/Select'
import type { ConfigurationMode } from '#features/Configuration/ApiConfiguration'
import { useShowManCodeInformationOverlay } from '#features/ManCode/useShowManCodeInformationOverlay'
import ChevronRightIcon from '#icons/chevron-1-right-24.svg'
import CloseIcon from '#icons/close-48.svg'
import DetailsIcon from '#icons/info-circle-outline-48.svg'
import PlusIcon from '#icons/plus-24.svg'
import { makeConfigurationLocation } from '#pages/Configuration/components/ConfigurationNavigation'
import { useCanOpenMoreConfigurations } from '#pages/Configuration/hooks/useCanOpenMoreConfigurations'
import { fragments, style } from '#styles/index'
import { searchParamsFromApiConfig } from '#utils/parseApiConfigurationFromSearch'
import { testIds } from '#utils/testIds'
import { useTranslatedManCodeOptionEntries } from '#utils/useTranslatedManCodeOptionEntries'
import { validateManCodeId } from '#utils/validateManCodeId'

const headerClass = css`
  cursor: pointer;
  padding: 4px 4px 12px 4px;
  display: flex;
  align-items: center;
  align-self: flex-end;
  transition: all 250ms;

  &.disabled, &.disabled:hover {
    cursor: unset;
    color: ${style.theme.textDisabled};
  }

  &:hover {
    color: ${style.theme.textAccent};
    transition: all 250ms;
  }
`

const codeFormClass = css`
  display: flex;
  align-items: flex-start;
  justify-content: stretch;

  gap: 4px;

  padding: 0 16px 16px 16px;
  border-bottom: 1px solid ${style.theme.borderSurface};
  min-width: 500px;

  &._without-border {
    border-bottom: unset;
  }


  > ._input-group {
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
    max-width: 250px;
    
    > div {
      width: 100%;
    }
    
    input {
      height: 40px;
    }

    > ._clear {
      position: absolute;
      right: 12px;
      top: 0;
      height: 40px;
      opacity: 0;
      pointer-events: none;
      transition: 0.25s ease opacity;
      will-change: opacity;

      &._visible {
        pointer-events: unset;
        opacity: 1;
      }
    }
  }

  > ._details {
    height: 40px;
  }
  
  > ._select {
    flex: 1;
    > .select {
      height: 40px;

      > .value {
        ${fragments.textStyle.body.s.condensedBold}
      }
    }

    > .select-box {
      height: 40px;
      border: 2px solid ${style.theme.borderInput};

      > .entry {
        display: flex;
        gap: 4px;
        align-items: center;
      } 
    }
  }

  > ._button {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
  }
`

export interface ManCodeFieldProps {
  manCode: string | undefined
  setManCode: Dispatch<SetStateAction<string | undefined>>
  withoutManCodeInfo?: boolean
  withoutModeSelector?: boolean
  withoutBorder?: boolean
  className?: string
}

export const ManCodeField: FC<ManCodeFieldProps> = ({
  withoutManCodeInfo = false,
  withoutModeSelector = false,
  className,
  withoutBorder,
  ...props
}) => {
  const [innerManCode, setInnerManCode] = useState<string | undefined>(undefined)
  const [mode, setMode] = useState<ConfigurationMode>('read-only')
  const { manCode, setManCode } = props.manCode ? props : { manCode: innerManCode, setManCode: setInnerManCode }

  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const id = useMemo(() => createLocalConfigurationId(), [])
  const concreteConfiguration = useAppSelector((x) => x.truckConfigurations.configurations[id])
  const { navigate } = useAppNavigation()
  const showManCode = useShowManCodeInformationOverlay()

  const loadManCode = useCallback(async () => {
    dispatch(
      addPendingConfiguration({
        id,
        callingApiConfiguration: {
          // do NOT apply the stored api configuration with a manCode
          manCode: manCode as EnhancedManCodeId,
          mode: mode,
          source: 'user-input',
        },
      }),
    )
  }, [dispatch, id, manCode, mode])

  useEffect(() => {
    if (concreteConfiguration?.creation.loadingStep === 'done') {
      navigate(makeConfigurationLocation(concreteConfiguration))
    }
  }, [concreteConfiguration, navigate])

  const options = useTranslatedManCodeOptionEntries()

  const validationError = validateManCodeId(manCode, t)
  const enabled = !!manCode && !validationError

  return (
    <div className={cx(codeFormClass, className, withoutBorder && '_without-border')}>
      <div className="_input-group">
        <Input
          kind="text"
          slim
          placeholder={t('setup.code.input-placeholder', 'MAN-Code (TMS-…)')}
          error={validationError}
          value={manCode ?? ''}
          onChange={(evt) => setManCode(evt.currentTarget.value)}
          className="_input"
          prepareErrorMargins={true}
          prepareLabelMargins={false}
          data-testid={testIds.setup.manCodeInput}
        />
        <IconButton
          className={cx('_clear', !!manCode && '_visible')}
          iconUrl={CloseIcon}
          title={''}
          onClick={(e) => {
            setManCode(undefined)
            ;(e.currentTarget as HTMLElement).blur()
          }}
        />
      </div>
      {!withoutManCodeInfo && (
        <IconButton
          className="_details"
          iconSize="l"
          iconUrl={DetailsIcon}
          title={t('code-table.actions.show-code-details', 'Show Code Details')}
          disabled={!enabled}
          onClick={(evt) => {
            if (!manCode) {
              return
            }
            evt.preventDefault()
            evt.stopPropagation()
            showManCode(manCode as EnhancedManCodeId)
          }}
        />
      )}
      {!withoutModeSelector && (
        <Select
          className="_select"
          // styleClassName="style-plain-button"
          options={options}
          value={mode}
          onChange={(item) => {
            item && setMode(item)
          }}
          slim
          data-testid={testIds.manCode.header.modeChooser}
        />
      )}
      <Button onClick={loadManCode} disabled={!enabled} className="_button" kind="primary" data-testid={testIds.manCode.header.loadButton}>
        {t('shared:common.button.load')} <Icon iconUrl={ChevronRightIcon} className="_icon" />
      </Button>
    </div>
  )
}

export const NewConfigurationTabEntry: FC = () => {
  const { t } = useTranslation()
  const { navigate, routes } = useAppNavigation()

  const canOpenMoreConfigurations = useCanOpenMoreConfigurations()
  const apiConfiguration = useAppSelector((x) => x.truckConfigurations.apiConfiguration)

  return (
    <HoverTooltip.div
      className={cx(headerClass, !canOpenMoreConfigurations && 'disabled')}
      title={
        canOpenMoreConfigurations
          ? t('setup.header.create-new-configuration', 'Create new configuration')
          : t('configuration.messages.please-close-configurations')
      }
      onClick={
        canOpenMoreConfigurations
          ? () =>
              navigate(
                routes['configuration/new'].make({
                  searchParams: searchParamsFromApiConfig({
                    apiConfig: apiConfiguration,
                  }),
                }),
              )
          : undefined
      }
      data-testid={testIds.header.createConfiguration}
    >
      <Icon iconUrl={PlusIcon} size="m" />
    </HoverTooltip.div>
  )
}
